import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { STORAGE_KEYS } from '../../../constants'
import { Dict, LocalizedValue } from '../../../globalTypes'
import persistentStorage from '../../../helpers/PersistentStorage'
import { Button } from '../../buttons/Button/Button'
import { StepForm, StepFormStep } from '../../form/StepForm/StepForm'
import { Modal } from '../../misc/Modal/Modal'
import * as Styled from '../styled'
import {
  AnalyticsPageEventInfo,
  AnalyticsTrackEventInfo,
  ProductWizardType,
  SubmitOptionsType,
  UpdateCartItemParams,
  WizardData,
} from '../types'
import { StepChooseItemSku } from './components/StepChooseItem/StepChooseItemSku'
import StepQuestionnaire from './components/StepQuestionnaire/StepQuestionnaire'
import { StepResultSku } from './components/StepResult/StepResultSku'
import StepSelectProcoteWizard from './components/StepSelectProcote/StepSelectProcote'
import StepYieldAndPounds from './components/StepYieldAndPounds/StepYieldAndPounds'
import { isBasicCard } from './helpers'
import { getProcoteSubmitOptions } from './helpers/getProcoteSubmitOptions'
import { getProcoteUpdateCartItems } from './helpers/getProcoteUpdateCartItems'
import {
  ProcoteQuestionnaireType,
  ProcoteStepNameForAnalytics,
  ProcoteWizardNode,
  ProductWizardProcoteSteps,
} from './types'

const data: ProcoteQuestionnaireType = persistentStorage.getLocalData(STORAGE_KEYS.ProcoteQuestionnaire)

export type BasicCard = {
  id: string
  description_i18n: LocalizedValue
  skus: string[]
  title_i18n: LocalizedValue
}

export type ProcoteSkuProps<AllowChooseExactCard extends boolean = false, CardType extends BasicCard = BasicCard> = {
  opened: boolean
  onClose: () => void
  onResultCardChange?: (cardId: string, skuId: string) => void
  isMobile?: boolean
  handleGoToCart?: () => void
  handleMarketAddProduct?: (
    cardId: string,
    skuId: string,
    options?: SubmitOptionsType<ProductWizardType.Procote>,
  ) => void
  handleMarketUpdateProduct?: (params: UpdateCartItemParams<ProductWizardType.Procote>) => void
  productWizard: ProcoteWizardNode
  handleCrm?: (
    cardId: string,
    skuId: string,
    qty: number,
    comment?: string,
    data?: WizardData<ProductWizardType.Procote>,
  ) => void
  onSendAnalyticsPage?: (info: AnalyticsPageEventInfo) => void
  onSendAnalyticsTrack?: (info: AnalyticsTrackEventInfo) => void
  allowChooseExactCard?: AllowChooseExactCard
} & (AllowChooseExactCard extends true
  ? {
      cards: CardType[]
    }
  : {
      cards?: CardType[]
    })

export const ProcoteSku = <AllowChooseExactProduct extends boolean = false, CardType extends BasicCard = BasicCard>({
  handleCrm,
  productWizard,
  opened,
  onClose,
  isMobile,
  handleGoToCart,
  handleMarketAddProduct,
  handleMarketUpdateProduct,
  onResultCardChange,
  cards = [],
  allowChooseExactCard,
  onSendAnalyticsPage,
  onSendAnalyticsTrack,
}: ProcoteSkuProps<AllowChooseExactProduct, CardType>) => {
  const { t } = useTranslation('procote')

  const [step, setStep] = useState('')
  const [showQuestionnaire, setShowQuestionnaire] = useState(false)
  const [wizardNodeId, setWizardNodeId] = useState<string | null>(null)
  // Actually it's wizard's node title, but it's kinda confusing to name it as
  // wizardNodeTitle because procote wizard nodes is named as categories
  const [categoryName, setCategoryName] = useState<string | null>(null)
  const [expectedYield, setExpectedYield] = useState(0)
  const [yieldUnit, setYieldUnit] = useState('')
  const [fertilizer, setFertilizer] = useState(0)
  const [acres, setAcres] = useState(0)
  const [skuId, setSkuId] = useState('')
  const [cardId, setCardId] = useState('')
  const [cardTitle, setCardTitle] = useState('')
  const [qty, setQty] = useState(0)
  const [userKnowsWhichProductIsNeeded, setUserKnowsWhichProductIsNeeded] = useState<boolean | null>(null)
  const [litresPerTonne, setLitresPerTonne] = useState(0)

  const nodesDict = useMemo(() => {
    const result: Dict<ProcoteWizardNode> = {}

    productWizard.children.forEach(node => {
      result[node.id] = node
    })

    return result
  }, [productWizard])

  const getSelectedNode = () => (wizardNodeId ? nodesDict[wizardNodeId] : null)

  const getWizardProducts = (): ProcoteWizardNode[] => {
    return getSelectedNode()?.children ?? []
  }

  const handleWizardClose = () => {
    setUserKnowsWhichProductIsNeeded(null)
    setWizardNodeId(null)
    setCategoryName(null)
    setExpectedYield(0)
    setFertilizer(0)
    setAcres(0)
    setCardId('')
    setCardTitle('')
    setSkuId('')
    setQty(0)
    setStep('')
    onClose()
  }

  const handleStepChooseItem = (item: CardType | ProcoteWizardNode) => {
    setStep(ProductWizardProcoteSteps.YieldAndPounds)

    if (isBasicCard(item)) {
      setCardId(item.id)
      setCardTitle(item.title_i18n.en || '')
      setSkuId(item.skus[0])
    } else {
      setWizardNodeId(item.id)
      setCategoryName(item.title)
    }
  }

  const handleStepResult = (cardId: string, skuId: string, qty: number, name?: string) => {
    setCardId(cardId)
    setQty(qty)
    name && setCardTitle(name)
    setSkuId(skuId)
    onResultCardChange?.(cardId, skuId)
  }

  const haveFertilizer = !!data?.[cardId]

  const handleAddToCart = () => {
    onSendAnalyticsTrack?.({
      step: ProcoteStepNameForAnalytics.Result,
      choice: 'add_to_cart',
    })

    const params = getProcoteSubmitOptions({
      qty,
      t,
      yieldUnit,
      litresPerTonne,
      categoryName,
      fertilizer,
      expectedYield,
      acres,
      cardId,
      cardTitle,
      skuId,
      haveFertilizer,
      steps: wizardNodeId ? [wizardNodeId] : [],
    })

    if (handleMarketAddProduct) {
      handleMarketAddProduct(cardId, skuId, params)
      setShowQuestionnaire(true)
      onClose()
      setStep(ProductWizardProcoteSteps.ChooseCrop)
    } else if (handleCrm) {
      handleCrm(cardId, skuId, qty, params.wizard_comment, params.wizard_data)
      handleWizardClose()
    }
  }

  const handleSubmitQuestionnaire = (haveFertilizerArg: boolean) => {
    const updatedParams = getProcoteUpdateCartItems({
      t,
      yieldUnit,
      litresPerTonne,
      categoryName,
      fertilizer,
      expectedYield,
      acres,
      cardId,
      cardTitle,
      skuId,
      haveFertilizer: haveFertilizerArg,
      steps: wizardNodeId ? [wizardNodeId] : [],
    })

    persistentStorage.changeItemFromLocalData(STORAGE_KEYS.ProcoteQuestionnaire, cardId, haveFertilizerArg)

    handleMarketUpdateProduct && handleMarketUpdateProduct(updatedParams)

    handleWizardClose()
    handleGoToCart && handleGoToCart()
  }

  const handleExpectedYield = (expectedYield: number, unit?: string) => {
    setExpectedYield(expectedYield)
    unit && setYieldUnit(unit)
  }

  const mapStepNames = () => {
    switch (true) {
      case step === ProductWizardProcoteSteps.ChooseItem && userKnowsWhichProductIsNeeded:
        return ProcoteStepNameForAnalytics.ChooseProcote
      case step === ProductWizardProcoteSteps.ChooseItem && !userKnowsWhichProductIsNeeded:
        return ProcoteStepNameForAnalytics.ChooseCrop
      case step === ProductWizardProcoteSteps.YieldAndPounds:
        return ProcoteStepNameForAnalytics.EnterPounds
      case step === ProductWizardProcoteSteps.Result:
        return ProcoteStepNameForAnalytics.Result
      default:
        return ProcoteStepNameForAnalytics.Unknown
    }
  }

  return (
    <>
      {allowChooseExactCard && (
        <StepSelectProcoteWizard
          opened={userKnowsWhichProductIsNeeded === null ? opened : false}
          onClose={handleWizardClose}
          isMobile={isMobile}
          setUserKnowsWhichProductIsNeeded={setUserKnowsWhichProductIsNeeded}
          setStep={setStep}
          onSendAnalyticsPage={onSendAnalyticsPage}
          onSendAnalyticsTrack={onSendAnalyticsTrack}
        />
      )}
      <Modal
        isOpen={!!step || (opened && !allowChooseExactCard)}
        onClose={() => {
          handleWizardClose()
          onSendAnalyticsTrack?.({
            step: mapStepNames(),
            action: 'close',
          })
        }}
        heading={t('title')}
        size={isMobile ? 'medium' : 'responsive'}
        modalPadding={'noPadding'}
      >
        <StepForm
          step={step ? step : !allowChooseExactCard ? ProductWizardProcoteSteps.ChooseItem : ''}
          completed={false}
          onTabClick={() => undefined}
          headerSize={'small'}
        >
          <StepFormStep
            id={ProductWizardProcoteSteps.ChooseItem}
            title={userKnowsWhichProductIsNeeded ? t('stepForm.chooseProcoteTitle') : t('stepForm.chooseCropTitle')}
          >
            <Styled.StepWrapper>
              <StepChooseItemSku
                wizardNodes={productWizard.children}
                cards={cards}
                handleNextStep={handleStepChooseItem}
                onSendAnalyticsPage={onSendAnalyticsPage}
                onSendAnalyticsTrack={onSendAnalyticsTrack}
                userKnowsWhichProductIsNeeded={!!userKnowsWhichProductIsNeeded}
              />
              <Styled.ButtonsWrapper>
                <Styled.BackButton
                  onClick={() => {
                    setStep('')
                    setUserKnowsWhichProductIsNeeded(null)
                    onSendAnalyticsTrack?.({
                      step:
                        userKnowsWhichProductIsNeeded && allowChooseExactCard
                          ? ProcoteStepNameForAnalytics.ChooseProcote
                          : ProcoteStepNameForAnalytics.ChooseCrop,
                      action: 'back',
                    })
                  }}
                />
              </Styled.ButtonsWrapper>
            </Styled.StepWrapper>
          </StepFormStep>
          <StepFormStep
            id={ProductWizardProcoteSteps.YieldAndPounds}
            title={userKnowsWhichProductIsNeeded ? t('stepForm.poundsTitle') : t('stepForm.yieldAndPoundsTitle')}
          >
            <Styled.StepWrapper>
              <StepYieldAndPounds
                getSelectedNode={getSelectedNode}
                expectedYield={expectedYield}
                setExpectedYield={handleExpectedYield}
                fertilizer={fertilizer}
                setFertilizer={setFertilizer}
                acres={acres}
                setAcres={setAcres}
                userKnowsWhichProductIsNeeded={!!userKnowsWhichProductIsNeeded}
                onSendAnalyticsPage={onSendAnalyticsPage}
              />
              <Styled.ButtonsWrapper>
                <Styled.BackButton
                  onClick={() => {
                    setStep(ProductWizardProcoteSteps.ChooseItem)
                    onSendAnalyticsTrack?.({
                      step: ProcoteStepNameForAnalytics.EnterPounds,
                      action: 'back',
                    })
                  }}
                />
                <Button
                  data-test-id={'next-btn'}
                  intent={'primary'}
                  filled
                  onClick={() => {
                    setStep(ProductWizardProcoteSteps.Result)
                    onSendAnalyticsTrack?.({
                      step: ProcoteStepNameForAnalytics.EnterPounds,
                      choice: `expected_yield: ${expectedYield}, fertilizer: ${fertilizer}, acres: ${acres}`,
                    })
                  }}
                  disabled={(!userKnowsWhichProductIsNeeded && !expectedYield) || !fertilizer || !acres}
                >
                  {t('stepForm.continue')}
                </Button>
              </Styled.ButtonsWrapper>
            </Styled.StepWrapper>
          </StepFormStep>
          <StepFormStep id={ProductWizardProcoteSteps.Result} title={t('stepForm.resultTitle')}>
            <Styled.StepWrapper>
              <StepResultSku
                getSelectedNode={getSelectedNode}
                getWizardProducts={getWizardProducts}
                expectedYield={expectedYield}
                fertilizer={fertilizer}
                acres={acres}
                categoryName={categoryName}
                handleResult={handleStepResult}
                cardId={cardId}
                skuId={skuId}
                cards={cards}
                userKnowsWhichProductIsNeeded={!!userKnowsWhichProductIsNeeded}
                setLitresPerTonne={setLitresPerTonne}
                onSendAnalyticsPage={onSendAnalyticsPage}
              />
              <Styled.ButtonsWrapper>
                <Styled.BackButton
                  onClick={() => {
                    setStep(ProductWizardProcoteSteps.YieldAndPounds)
                    onSendAnalyticsTrack?.({
                      step: ProcoteStepNameForAnalytics.Result,
                      action: 'back',
                    })
                  }}
                />
                <Button data-test-id={'next-btn'} intent={'primary'} filled onClick={handleAddToCart}>
                  {t('stepForm.addToCart')}
                </Button>
              </Styled.ButtonsWrapper>
            </Styled.StepWrapper>
          </StepFormStep>
        </StepForm>
      </Modal>
      {showQuestionnaire && (
        <StepQuestionnaire
          opened={showQuestionnaire}
          onClose={() => {
            handleWizardClose()
            setShowQuestionnaire(false)
          }}
          value={haveFertilizer}
          isMobile={isMobile}
          onSubmit={handleSubmitQuestionnaire}
          onGoToCart={() => {
            handleWizardClose()
            handleGoToCart && handleGoToCart()
          }}
          productName={cardTitle}
          qty={qty}
          onSendAnalyticsPage={onSendAnalyticsPage}
          onSendAnalyticsTrack={onSendAnalyticsTrack}
        />
      )}
    </>
  )
}
